import { ErrorHandler, NgModule, inject, provideAppInitializer } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from "@angular/common/http";
import { TranslateCompiler, TranslateModule } from "@ngx-translate/core";

import {
    ConsoleLogLevel,
    CoreModule,
    LG_APPLICATION_MULTI_EVENT_TRACERS,
    LgConsoleConfiguration,
    LgErrorHandler
} from "@logex/framework/core";

import { LgFiltersetModule } from "@logex/framework/lg-filterset";
import {
    LG_FW_UI_STATE_SERVICE,
    LG_SCENARIO_DIALOG_CONFIG,
    LG_SCENARIO_GATEWAY,
    LgLayoutModule,
    LgUserStorageFwUiStateService
} from "@logex/framework/lg-layout";
import { UiCoreModule } from "@logex/framework/ui-core";
import { UiToolboxModule } from "@logex/framework/ui-toolbox";

import {
    LG_LOCALIZATION_SETTINGS,
    LgLocalizationModule,
    ReferenceTranslateCompiler,
    useMessageFormatLocales
} from "@logex/framework/lg-localization";

import {
    DefinitionsApiBase,
    LG_APPLICATION_INSIGHTS_CONFIGURATION,
    LG_APP_CONFIGURATION,
    LG_APP_CONTROL,
    LG_APP_DEFINITIONS,
    // LG_APP_VERSION, AppVersionService,
    LG_APP_GET_PERIOD_NAME,
    LG_APP_INFO,
    LG_APP_SESSION,
    LG_AUTHENTICATION_SERVICE,
    LG_AUTHORIZATION_SERVICE,
    LG_MATOMO_CONFIGURATION,
    LG_MESSAGE_BUS_SERVICE,
    LG_NAVIGATION,
    LG_NAVIGATION_CHANGE_HANDLER,
    LG_POSTPONE_MATOMO_CONFIGURATION,
    LG_USERFLOW_CONFIGURATION,
    LG_USER_INFO,
    LG_USER_STORAGE_SERVICE_GATEWAY,
    LgAppControlV2Service,
    LgAppInitializerService,
    LgApplicationModule,
    LgApplicationPermissionsBase,
    LgAuthorizationInterceptor,
    LgAuthorizationServiceV2,
    LgMatTrackingService,
    LgMessageBusService,
    LgNavitationChangeHandlerEvaluatePermissions,
    LgScenarioInterceptor,
    LgUserStorageGatewayV2,
    LgUserflowConfiguration,
    OidcAuthService,
    VersionService
} from "@logex/framework/lg-application";
import { LgExportsModule } from "@logex/framework/lg-exports";
import { LgPivotTableModule } from "@logex/framework/lg-pivot-table";
import { navigation } from "@shared/app-navigation";
import { ApplicationPermissions } from "@shared/app-permissions";
import { AppUser } from "@shared/app-user";
import { SharedModule } from "@shared/shared.module";

import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { AuthorizedComponent } from "./authorized.component";

import { LgChartsModule } from "@logex/framework/lg-charts";
import { AppSession } from "@shared/app-session";
import { AppInfo } from "@shared/application/app-info";
import { AppScenarioGateway } from "@shared/scenario/app-scenario-gateway";
import { ScenarioService } from "@shared/scenario/scenario-service";
import { ApplicationInsightsConfiguration, ApplicationInsightsServiceExt } from "@shared/services";
import { AppLgLocalizationSettings } from "@shared/services/app-lg-localization-settings";
import { AppStartupService } from "@shared/services/app-startup.service";
import { AppConfiguration } from "@shared/services/app-startup.service.types";

export class Definition extends DefinitionsApiBase {
    constructor(_httpClient: HttpClient) {
        super(_httpClient);
    }
}

const getPeriodName = (version, showDataVersionPeriode?): string => {
    let prefix = "";
    switch (version.periode) {
        case 1:
            prefix = `Q2 ${version.jaar - 1} - Q1 `;
            break;
        case 2:
            prefix = `Q3 ${version.jaar - 1} - Q2 `;
            break;
        case 3:
            prefix = `Q4 ${version.jaar - 1} - Q3 `;
            break;
        case 4:
            prefix = `Q1 ${version.jaar}-Q4 `;
            break;
    }
    return prefix + version.jaar;
};

@NgModule({
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,
        TranslateModule.forRoot({
            compiler: { provide: TranslateCompiler, useClass: ReferenceTranslateCompiler }
        }),
        FormsModule,
        CoreModule,
        UiCoreModule,
        UiToolboxModule,
        LgLocalizationModule.forRoot({ localizationUrlPrefix: "localization" }),
        LgFiltersetModule.forRoot(),
        LgPivotTableModule,
        LgApplicationModule.forRoot(),
        LgExportsModule,
        LgChartsModule.forRoot({ useNewColorPalette: true }),
        LgLayoutModule,
        SharedModule,
        // LgTicketManagerModule,

        AppRoutingModule // last!
    ],
    declarations: [AppComponent, AuthorizedComponent],
    providers: [
        {
            provide: LgConsoleConfiguration,
            useFactory: () => {
                const config = new LgConsoleConfiguration();
                config.logger("Logex.Application", ConsoleLogLevel.Error);
                return config;
            }
        },
        { provide: LG_USER_INFO, useValue: new AppUser() },
        { provide: LG_SCENARIO_GATEWAY, useClass: AppScenarioGateway },
        { provide: LG_APP_CONFIGURATION, useValue: new AppConfiguration() },
        { provide: LG_APP_SESSION, useValue: new AppSession() },
        { provide: LG_APP_INFO, useValue: new AppInfo() },
        {
            provide: LG_SCENARIO_DIALOG_CONFIG,
            useValue: {
                showScenarioPeriodName: true,
                allowScenarioDelete: false,
                allowScenarioCreate: false,
                reloadOnScenarioChange: true
            }
        },
        useMessageFormatLocales(["nl", "nl-NL", "en", "en-GB"]),
        { provide: LG_APP_GET_PERIOD_NAME, useValue: getPeriodName },
        {
            provide: LG_APP_DEFINITIONS,
            useFactory: (_httpClient: HttpClient) => new Definition(_httpClient),
            deps: [HttpClient]
        },
        { provide: LG_NAVIGATION, useFactory: () => navigation(), deps: [ApplicationPermissions] },
        { provide: LgApplicationPermissionsBase, useExisting: ApplicationPermissions },
        {
            provide: LG_NAVIGATION_CHANGE_HANDLER,
            useClass: LgNavitationChangeHandlerEvaluatePermissions
        },
        { provide: ErrorHandler, useClass: LgErrorHandler },
        { provide: LG_AUTHORIZATION_SERVICE, useClass: LgAuthorizationServiceV2 },
        { provide: LG_AUTHENTICATION_SERVICE, useClass: OidcAuthService },
        { provide: LG_MESSAGE_BUS_SERVICE, useClass: LgMessageBusService },
        { provide: LG_APP_CONTROL, useClass: LgAppControlV2Service },
        { provide: HTTP_INTERCEPTORS, useClass: LgAuthorizationInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LgScenarioInterceptor, multi: true },
        { provide: LG_USER_STORAGE_SERVICE_GATEWAY, useClass: LgUserStorageGatewayV2 },
        { provide: LG_LOCALIZATION_SETTINGS, useClass: AppLgLocalizationSettings },
        { provide: VersionService, useExisting: ScenarioService },
        provideAppInitializer(() => inject(AppStartupService).initialize()),
        { provide: LG_MATOMO_CONFIGURATION, useValue: LG_POSTPONE_MATOMO_CONFIGURATION },
        {
            provide: LG_APPLICATION_INSIGHTS_CONFIGURATION,
            useExisting: ApplicationInsightsConfiguration
        },
        {
            provide: LG_APPLICATION_MULTI_EVENT_TRACERS,
            useExisting: ApplicationInsightsServiceExt,
            multi: true
        },
        {
            provide: LG_APPLICATION_MULTI_EVENT_TRACERS,
            useClass: LgMatTrackingService,
            multi: true
        },
        { provide: LG_FW_UI_STATE_SERVICE, useExisting: LgUserStorageFwUiStateService },
        { provide: LG_USERFLOW_CONFIGURATION, useClass: LgUserflowConfiguration },
        {
            provide: LgAppInitializerService,
            useValue: { initialize: () => Promise.resolve() } as any
        }
    ],
    bootstrap: [AppComponent]
})
export class AppModule {}
